// Confirm-and-continue for chat actions (ADR 46-G / F2). When the user approves
// a state-changing tool the assistant proposed, we RUN it and RESUME the turn so
// the assistant acknowledges the real result — instead of the old fire-and-forget
// confirm that left the conversation frozen. A module-level singleton over the
// shared chat store, so the deeply-nested ConfirmCard can trigger it directly
// (like it imported confirmChatTool before).
//
// The gateway executes the tool inside POST /completion/stream (body.confirm),
// emits a `confirm_result`, injects the result into the turn, and streams the
// model's follow-up — which we render as a NEW assistant message.

import { useChatStore, type TraceBlock } from '../stores/chat';
import { streamChatCompletion, type ChatStreamEvent } from '../api/client';
import { buildGlobalSystem } from '../designer/chatContext';

export interface ConfirmOutcome { ok: boolean; detail?: string }

function traceText(blocks: TraceBlock[]): string {
  return blocks.filter((b) => b.type === 'text' && b.text).map((b) => b.text).join('\n');
}

/** Apply one stream event to the follow-up message's blocks (compact mirror of
 *  useChatTurn.handleEvent — only the events a continuation can produce). */
function applyEvent(
  ev: ChatStreamEvent,
  blocks: TraceBlock[],
  current: TraceBlock | null,
  confirmBlock: TraceBlock,
  setOutcome: (o: ConfirmOutcome) => void,
): TraceBlock | null {
  switch (ev.kind) {
    case 'confirm_result':
      confirmBlock.status = ev.ok ? 'done' : 'error';
      setOutcome({ ok: ev.ok, detail: ev.detail ?? ev.error });
      return current;
    case 'text_delta': {
      let cur = current;
      if (!cur || cur.type !== 'text') { blocks.push({ type: 'text', text: '' }); cur = blocks[blocks.length - 1]!; }
      cur.text = (cur.text ?? '') + ev.text;
      return cur;
    }
    case 'tool_call':
      blocks.push({ type: 'tool_use', tool_call_id: ev.tool_call_id, name: ev.name, input: ev.input });
      return null;
    case 'tool_result':
      blocks.push({ type: 'tool_result', tool_call_id: ev.tool_call_id, output: ev.content, is_error: ev.is_error });
      return null;
    // A chained mutation in the follow-up → render another confirm card.
    case 'confirm_request':
      blocks.push({ type: 'confirm', tool_call_id: ev.tool_call_id, name: ev.name, input: ev.input, status: 'pending' });
      return null;
    case 'error':
      setOutcome({ ok: false, detail: ev.message });
      return current;
    default:
      return current;
  }
}

/** Run the user-approved tool and stream the assistant's acknowledgment as a new
 *  message. Resolves with the tool outcome (drives the ConfirmCard's final state). */
export async function confirmAndContinue(block: TraceBlock): Promise<ConfirmOutcome> {
  if (!block.name) return { ok: false, detail: 'no tool' };
  const store = useChatStore();

  // Text-collapsed history BEFORE pushing the follow-up (mirrors useChatTurn.send).
  const history = store.messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: (typeof m.content === 'string' ? m.content : traceText(m.content as TraceBlock[])) as unknown,
    }));

  const sessionId = store.activeId;
  const liveMsg = store.pushLocal({ role: 'assistant', content: [] as TraceBlock[] });
  const blocks = liveMsg.content as TraceBlock[];

  let outcome: ConfirmOutcome = { ok: true };
  let current: TraceBlock | null = null;
  const ac = new AbortController();
  try {
    await streamChatCompletion(
      { messages: history, system: buildGlobalSystem(), confirm: { name: block.name, arguments: block.input ?? {} } },
      (ev) => { current = applyEvent(ev, blocks, current, block, (o) => { outcome = o; }); },
      ac.signal,
    );
  } catch (err) {
    outcome = { ok: false, detail: err instanceof Error ? err.message : String(err) };
  }

  const finalText = traceText(blocks);
  if (finalText && sessionId) {
    try { await store.persist({ role: 'assistant', content: finalText }, sessionId); } catch { /* keep in memory */ }
  }
  return outcome;
}
